/**
 * Apollo Client Configuration
 *
 * GraphQL client for the 206mp frontend application.
 * Uses the same URL builder pattern as REST API for consistency.
 *
 * Feature: 062-graphql-api
 */

import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import logger from '../utils/logger';

// HTTP link to GraphQL endpoint
// Use relative URL to leverage nginx proxy in production/Railway
// In development, Vite dev server proxies /api/* to the API service (vite.config.ts)
const httpLink = createHttpLink({
  uri: '/api/graphql',
});

// Auth link - adds JWT token to requests
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      logger.error('GraphQL', 'GraphQL error', {
        message,
        locations,
        path,
        extensions,
        operationName: operation.operationName,
      });

      // Handle unauthorized errors
      if (extensions?.code === 'UNAUTHENTICATED') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    });
  }

  if (networkError) {
    logger.error('GraphQL', 'Network error', {
      message: networkError.message,
      operationName: operation.operationName,
    });
  }
});

// Logging link for debugging
const loggingLink = new ApolloLink((operation, forward) => {
  const startTime = Date.now();

  logger.debug('GraphQL', 'Request', {
    operationName: operation.operationName,
    variables: operation.variables,
  });

  return forward(operation).map((response) => {
    const duration = Date.now() - startTime;

    logger.debug('GraphQL', 'Response', {
      operationName: operation.operationName,
      duration: `${duration}ms`,
      hasErrors: !!response.errors,
    });

    return response;
  });
});

// Create Apollo Client instance
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, loggingLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Cache object lookups by ID
          user: {
            keyArgs: ['id'],
          },
          photo: {
            keyArgs: ['id'],
          },
          collection: {
            keyArgs: ['id'],
          },
          // Cache collections list by page/limit
          collections: {
            keyArgs: ['page', 'limit'],
          },
          // Cache photos list by userId
          photos: {
            keyArgs: ['userId', 'limit', 'offset'],
          },
          // Cache critique sessions by collectionId
          critiqueSessions: {
            keyArgs: ['collectionId', 'limit'],
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  },
  // Enable Apollo DevTools in development
  connectToDevTools: import.meta.env.DEV,
});
